import { isValidElement } from 'react';
import type { ReactNode } from 'react';
import type { RenderItemResult } from '../types';

export interface LimitedItems<TItem> {
  renderItems: TItem[];
  renderFurtherMessage: boolean;
}

/**
 * Filters the items using the optional predicate. When no predicate is given,
 * every item is kept (matching the original default `() => true`).
 */
export const filterItems = <TItem>(items: TItem[], value: string, shouldItemRender?: (item: TItem, value: string) => boolean): TItem[] => {
  const filterFn = typeof shouldItemRender === 'function' ? shouldItemRender : () => true;
  return items.filter((item) => filterFn(item, value));
};

/**
 * Caps the list at `limit` entries. When the list is truncated, a "further
 * results" message should be shown.
 */
export const limitItems = <TItem>(items: TItem[], limit?: number): LimitedItems<TItem> => {
  if (limit && items.length > limit) {
    return { renderItems: items.slice(0, limit), renderFurtherMessage: true };
  }
  return { renderItems: items, renderFurtherMessage: false };
};

/**
 * Clamps a candidate highlighted index into the valid range, accounting for the
 * configured `limit` and whether a "further results" row is present (that row
 * occupies the slot directly after the last item).
 *
 * Preserves the exact bounds logic of the original component.
 */
export const normalizeHighlightedIndex = (index: number, renderItemsLength: number, hasFurtherMessage: boolean, limit?: number): number => {
  let normalized = index;

  if (normalized < -1) {
    normalized = -1;
  } else if (limit && limit > 0) {
    if (hasFurtherMessage && normalized > limit) {
      normalized = limit;
    } else if (!hasFurtherMessage && normalized > limit - 1) {
      normalized = limit - 1;
    }
  }

  if (normalized >= renderItemsLength && !hasFurtherMessage) {
    normalized = renderItemsLength - 1;
  } else if (normalized > renderItemsLength && hasFurtherMessage) {
    normalized = renderItemsLength;
  }

  return normalized;
};

const isWrappedRenderResult = (result: RenderItemResult): result is { item?: ReactNode; skipClickEvent?: boolean } => {
  return typeof result === 'object' && result !== null && !Array.isArray(result) && !isValidElement(result) && ('item' in result || 'skipClickEvent' in result);
};

/**
 * Mirrors the original component's dual-contract handling of a render result:
 * a render fn may return a plain node, or `{ item, skipClickEvent }`. Returns
 * the node to render and whether selection-on-click should be skipped.
 */
export const extractRenderParts = (result: RenderItemResult): { node: ReactNode; skipClickEvent: boolean } => {
  if (isWrappedRenderResult(result)) {
    return {
      node: result.item ? result.item : (result as ReactNode),
      skipClickEvent: !!result.skipClickEvent,
    };
  }
  return { node: result, skipClickEvent: false };
};
